import React from 'react';
import { colors } from '@/assets/styles/colors';


export interface DonutSegment {
  percentage: number;
  color: string;
}

interface DonutChartProps {
  percentage?: number;
  color?: string;
  segments?: DonutSegment[];
  labelColor?: string;
  size?: number;
  strokeWidth?: number;
  label?: string;
  subLabel?: string;
  'data-testid'?: string;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  percentage,
  color,
  segments,
  labelColor,
  size = 195,
  strokeWidth = 18,
  label,
  subLabel,
  'data-testid': dataTestId,
}) => {
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const resolvedSegments: DonutSegment[] = segments ?? (
    percentage !== undefined && color ? [{ percentage, color }] : []
  );

  const resolvedLabelColor = labelColor ?? resolvedSegments[0]?.color ?? colors.grays.neutral66;

  let cumulativePercentage = 0;
  const arcs = resolvedSegments.map((seg) => {
    const startPercentage = cumulativePercentage;
    cumulativePercentage += seg.percentage;
    return { seg, startPercentage };
  });

  return (
    <div
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      data-testid={dataTestId}
    >
      <svg width={size} height={size}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#D9D9D9"
          strokeWidth={strokeWidth}
        />
        {arcs.map(({ seg, startPercentage }, index) => {
          const segLength = (seg.percentage / 100) * circumference;
          const rotationDeg = -90 + (startPercentage / 100) * 360;
          return (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segLength} ${circumference}`}
              strokeDashoffset={0}
              strokeLinecap="butt"
              transform={`rotate(${rotationDeg}, ${center}, ${center})`}
            />
          );
        })}
      </svg>

      {label && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <span
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: size >= 180 ? 32 : 28,
              color: resolvedLabelColor,
              lineHeight: 1,
            }}
          >
            {label}
          </span>
          {subLabel && (
            <span
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 400,
                fontSize: 11,
                color: colors.grays.neutral66,
                lineHeight: 1,
              }}
            >
              {subLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
